import { getSymbol, Modal, roundNumber, SubmitButton, Table } from "@xola/ui-kit";
import { AxiosError } from "axios";
import clsx from "clsx";
import { FormField, getPaymentMethodName, isStoreCredit } from "common";
import { round, sumBy } from "lodash";
import React, { useState } from "react";
import {
    ExperiencePurchaseItem,
    LineItemObject,
    LineItemType,
    MerchandisePurchaseItem,
    Payment,
    PaymentMethod,
    Purchase,
} from "types";
import { MembershipPurchaseItem } from "types/src/types/MembershipPurchaseItem";
import { array, number, object } from "yup";
import { findValue } from "../../api/interceptors";
import { useSeller } from "../../hooks/useAccount";
import { getLineItemsGroupedByPaymentMethods } from "../../models/Purchase";
import { Currency } from "../Common/Currency";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { CurrencyInputField } from "../Field/CurrencyInputField";
import { PaymentBreakdown } from "./PaymentBreakdown";

interface TransferToStoreCreditModalProps {
    isOpen: boolean;
    isLoading: boolean;
    onClose: () => void;
    purchase: Purchase;
    item: ExperiencePurchaseItem | MembershipPurchaseItem | MerchandisePurchaseItem | null;
    shouldReduce: boolean;
    notify: boolean;
    serviceFeeDelta: number;
    onSubmit: (payload: any, amountToReturn: number) => void;
    amount: number;
}

export const TransferToStoreCreditModal = ({ isOpen, onClose, ...rest }: TransferToStoreCreditModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <ModalContent onClose={onClose} {...rest} />
        </Modal>
    );
};

interface FormValue {
    payment?: Payment;
    amount: number;
    travelerName?: string;
    // eslint-disable-next-line react/no-unused-prop-types
    value: number;
}

const ModalContent = ({
    onClose,
    purchase,
    isLoading,
    item,
    shouldReduce,
    notify,
    serviceFeeDelta,
    onSubmit,
    amount,
}: Omit<TransferToStoreCreditModalProps, "isOpen">) => {
    const seller = useSeller();
    const payments = getLineItemsGroupedByPaymentMethods(purchase, true, false);

    const [error, setError] = useState("");

    const initialValues = Object.getOwnPropertyNames(payments).map((paymentMethod) => {
        const lineItems = payments[paymentMethod as PaymentMethod] ?? [];
        return {
            payment: lineItems[0]?.payment,
            amount: round(Math.abs(sumBy(lineItems, "amount")), 2),
            travelerName: lineItems[0]?.traveler?.name,
            value: 0,
        };
    });

    const handleSubmit = async (values: FormValue[]) => {
        const payload = getPayload(purchase, item, values, shouldReduce, notify, serviceFeeDelta);
        onSubmit(payload, getAmountToReturn(values));
    };

    const validate = (values: FormValue[]) => {
        const formTotal = roundNumber(purchase.currency, sumBy(values, "value"));
        return amount === formTotal
            ? {}
            : { total: `Total must equal ${getSymbol(seller.currency)}${amount.toFixed(2)}` };
    };

    const handleError = (error: AxiosError, isValid: boolean) => {
        if (isValid) {
            setError(findValue(error?.response?.data, "message"));
        }
    };

    const handleSuccess = (response: Purchase | null) => {
        if (!response) {
            return;
        }

        setError("");
    };

    return (
        <XolaFormik
            validateOnChange
            initialValues={initialValues}
            validationSchema={getValidationSchema()}
            validate={validate}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, errors, values, isValid }) => {
                const total = sumBy(values, (row) => Number(row.value || 0));
                const formError = (errors as any).total;

                return (
                    <>
                        <Modal.Header data-testid="transfer-to-store-credit-modal-header">
                            Transfer Amount to Store Credit
                        </Modal.Header>
                        <Modal.Body data-testid="transfer-to-store-credit-modal-body">
                            <div className="space-y-4">
                                <PaymentBreakdown
                                    isEnabled
                                    payload={getPayload(purchase, item, values, shouldReduce, notify, serviceFeeDelta)}
                                    purchase={purchase}
                                    item={item ?? undefined}
                                    onUpdate={handleSuccess}
                                    onError={(error) => handleError(error, isValid)}
                                />
                                <Table data-testid="transfer-to-store-credit-table" className="border-none">
                                    <Table.Head>
                                        <Table.Row>
                                            <Table.Header>Name</Table.Header>
                                            <Table.Header>Amount Paid</Table.Header>
                                            <Table.Header>Payment Method</Table.Header>
                                            <Table.Header>Refund Value</Table.Header>
                                        </Table.Row>
                                    </Table.Head>
                                    <Table.Body>
                                        {values.map((formValue, index) => {
                                            return (
                                                <LineItemTableRow
                                                    key={formValue.payment?.method}
                                                    index={index}
                                                    {...formValue}
                                                />
                                            );
                                        })}
                                    </Table.Body>
                                </Table>
                            </div>

                            <div
                                data-testid="transfer-to-store-credit-total"
                                className={clsx("mt-2 flex justify-end pr-4 font-bold", !!formError && "text-red")}
                            >
                                Total Refund Value: {getSymbol(seller.currency)}
                                <var data-var="total">{round(total, 2).toFixed(2)}</var>
                            </div>

                            {!!formError && (
                                <>
                                    <div
                                        data-testid="transfer-to-store-credit-error"
                                        className="flex justify-end pr-4 text-sm text-red"
                                    >
                                        {formError}
                                    </div>
                                    <div className="flex justify-end pr-4 text-sm text-red">{error}</div>
                                </>
                            )}
                        </Modal.Body>

                        <Modal.Footer className="flex justify-end space-x-4">
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                            <SubmitButton isLoading={isLoading} onClick={handleSubmit}>
                                Refund to Store Credit
                            </SubmitButton>
                        </Modal.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};

interface LineItemTableRowProps {
    index: number;
}

const LineItemTableRow = ({ travelerName, amount, payment, index }: FormValue & LineItemTableRowProps) => {
    const seller = useSeller();

    return (
        <Table.Row>
            <Table.Cell className="no-translate">{travelerName}</Table.Cell>
            <Table.Cell>
                <Currency>{amount}</Currency>
            </Table.Cell>
            <Table.Cell>{payment?.method && getPaymentMethodName(seller, payment?.method)}</Table.Cell>
            <Table.Cell>
                <FormField name={`${index}.value`}>
                    <CurrencyInputField data-testid="line-item-table-row-refund-value-input" />
                </FormField>
            </Table.Cell>
        </Table.Row>
    );
};

const getValidationSchema = () => {
    return array().of(
        object().shape({
            value: number()
                .required("must be a positive number up to two decimal places")
                .when("amount", (amount, schema) => schema.max(amount, "must not exceed amount paid")),
        }),
    );
};

const getAmountToReturn = (values: FormValue[]) => {
    return sumBy(values, "amount");
};

const getPayload = (
    purchase: Purchase,
    selectedItem: ExperiencePurchaseItem | MembershipPurchaseItem | MerchandisePurchaseItem | null,
    values: FormValue[],
    shouldReduce: boolean,
    notify: boolean,
    serviceFeeDelta: number,
) => {
    const decreaseAmount = sumBy(values, (row) => Number(row.value));
    const item = selectedItem ?? purchase.items[0];

    const refundLineItems = getRefundAmountPayloadOps(values, purchase);

    const ops: any[] = [
        {
            path: `/items/${item.id}/lineItems`,
            op: "addLineItem",
            data: {
                amount: decreaseAmount === 0 ? 0 : (decreaseAmount - serviceFeeDelta) * -1,
                name: "Reduce booking value", // Send a default name if empty
                object: LineItemObject.DEFAULT,
                type: LineItemType.ADJUSTMENT,
            },
        },
        ...refundLineItems,
    ];

    if (!shouldReduce) {
        // The user only wants to return payment, so remove the element at the top
        ops.shift();
    }

    return {
        ops,
        _emailOverrides: { sendEmailToTraveler: notify },
    };
};

const getRefundAmountPayloadOps = (values: FormValue[], purchase: Purchase) => {
    return values
        .filter((value) => value.value > 0)
        .map((value) => {
            const amount = calculateAmount(purchase, value.value);
            const paymentMethod = value.payment?.method;
            const item = getStoreCreditOperation(amount, paymentMethod);
            if (paymentMethod === PaymentMethod.CREDIT_CARD) {
                (item.data.payment as Payment).card = value.payment?.card;
            }

            return item;
        });
};

export const getRefundAmountToStoreCredit = (purchase: Purchase) => {
    // The purchase is a store credit we are trying to refund aka Transfer out
    let refundAmount = 0;
    const lineItems = new Set([LineItemObject.PAYMENT, LineItemObject.REFUND]);

    for (const lineItem of purchase.lineItems) {
        if (lineItems.has(lineItem.object) && !(isStoreCredit(purchase) && "fundingSource" in lineItem)) {
            refundAmount += lineItem.amount;
        }
    }

    return roundNumber(purchase.currency, Math.abs(refundAmount));
};

const calculateAmount = (purchase: Purchase, amountFormValue: number) => {
    // Adjust for flex level edge case when user enters the amount with fee
    const displayedMaxAmount = getRefundAmountToStoreCredit(purchase);

    return Math.min(displayedMaxAmount, amountFormValue);
};

export const getStoreCreditOperation = (amount: number, paymentMethod?: PaymentMethod | ID, name?: string) => {
    return {
        path: "/lineItems",
        op: "addLineItem",
        data: {
            amount,
            name: "Return payment",
            object: LineItemObject.REFUND,
            payload: LineItemType.REFUND,
            payment: { method: paymentMethod, ...(name ? { comment: name } : null) },
            refundToStoreCredit: true,
        },
    };
};
